import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_STRIPE_ACCOUNT } from "./mutations";
import { SUPPORTED_STRIPE_COUNTRIES } from "../../constants/vars";
import Select from "react-select";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function CreateStripeAccountModal({ onCreated, onClose }) {
    const scopeId = "kbola-create-stripe-account";
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [country, setCountry] = useState("US");

    const [createLink, { loading }] = useMutation(CREATE_STRIPE_ACCOUNT, {
        onCompleted: (data) => {
            const url = data?.createExpressOnboardingLink?.url;
            const account = data?.createExpressOnboardingLink?.stripeAccount;

            if (url) {
                toast.success(t("createStripeAccountModal.redirecting"));
                if (onCreated) onCreated(account);
                window.open(url, "_blank");
                onClose();
            } else {
                toast.error(t("createStripeAccountModal.onboardingLinkFailed"));
            }
        },
        onError: (error) => {
            toast.error(getLocalizedGraphQLError(error, t, "createStripeAccountModal.somethingWentWrong"));
        },
    });

    const handleCreate = () => {
        if (!name.trim()) {
            toast.warning(t("createStripeAccountModal.enterNameWarning"));
            return;
        }
        if (!country) {
            toast.warning(t("createStripeAccountModal.selectCountryWarning"));
            return;
        }

        createLink({ variables: { name, country } });
    };

    const selectStyles = useMemo(() => {
        const focusRing = "0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18)";
        const border = "1px solid rgba(0,0,0,0.10)";

        return {
            control: (base, state) => ({
                ...base,
                minHeight: 44,
                borderRadius: 12,
                border: state.isFocused ? "1px solid rgba(0,0,0,0.18)" : border,
                boxShadow: state.isFocused ? focusRing : "none",
                backgroundColor: "#fff",
                cursor: "pointer",
                transition: "box-shadow 120ms ease, border-color 120ms ease",
                ":hover": {
                    border: "1px solid rgba(0,0,0,0.18)",
                },
            }),
            valueContainer: (base) => ({
                ...base,
                padding: "2px 12px",
            }),
            input: (base) => ({
                ...base,
                margin: 0,
                padding: 0,
                color: "#14151a",
                fontWeight: 800,
            }),
            singleValue: (base) => ({
                ...base,
                color: "#14151a",
                fontWeight: 900,
            }),
            placeholder: (base) => ({
                ...base,
                color: "rgba(20,21,26,0.45)",
                fontWeight: 800,
            }),
            indicatorsContainer: (base) => ({
                ...base,
                paddingRight: 6,
            }),
            dropdownIndicator: (base, state) => ({
                ...base,
                color: state.isFocused ? "rgba(20,21,26,0.70)" : "rgba(20,21,26,0.45)",
                ":hover": { color: "rgba(20,21,26,0.70)" },
            }),
            clearIndicator: (base) => ({
                ...base,
                color: "rgba(20,21,26,0.45)",
                ":hover": { color: "rgba(20,21,26,0.70)" },
            }),
            indicatorSeparator: () => ({ display: "none" }),
            menu: (base) => ({
                ...base,
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.10)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.14)",
                zIndex: 9999,
            }),
            menuList: (base) => ({
                ...base,
                padding: 8,
            }),
            option: (base, state) => ({
                ...base,
                borderRadius: 12,
                margin: "4px 0",
                padding: "10px 12px",
                fontWeight: 900,
                cursor: "pointer",
                backgroundColor: state.isSelected
                    ? "rgba(var(--kbs-primary-rgb, 0,173,255), 0.85)"
                    : state.isFocused
                        ? "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)"
                        : "#fff",
                color: "#14151a",
            }),
        };
    }, []);

    const ui = useMemo(() => {
        return {
            sectionTitle: {
                fontSize: 12.5,
                fontWeight: 900,
                color: "rgba(20,21,26,0.72)",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                marginBottom: 6,
            },
            helper: {
                marginTop: 6,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            input: {
                height: 44,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 900,
                color: "#14151a",
            },
            footer: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                paddingTop: 12,
                borderTop: "1px solid rgba(0,0,0,0.06)",
                marginTop: 14,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDisabled: {
                opacity: 0.65,
                cursor: "not-allowed",
            },
        };
    }, []);

    return (
        <div id={scopeId}>
            <div className="form-group">
                <label htmlFor="stripeName" style={ui.sectionTitle}>
                    {t("createStripeAccountModal.accountNickname")}
                </label>
                <input
                    id="stripeName"
                    type="text"
                    className="form-control"
                    style={ui.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("createStripeAccountModal.accountNicknamePlaceholder")}
                    autoComplete="off"
                />
                <div style={ui.helper}>{t("createStripeAccountModal.accountNicknameHelper")}</div>
            </div>

            <div className="form-group">
                <label htmlFor="stripeCountry" style={ui.sectionTitle}>
                    {t("createStripeAccountModal.country")}
                </label>
                <Select
                    inputId="stripeCountry"
                    options={SUPPORTED_STRIPE_COUNTRIES}
                    value={SUPPORTED_STRIPE_COUNTRIES.find((opt) => opt.value === country)}
                    onChange={(option) => setCountry(option?.value)}
                    placeholder={t("createStripeAccountModal.countryPlaceholder")}
                    isSearchable
                    styles={selectStyles}
                />
                <div style={ui.helper}>{t("createStripeAccountModal.countryHelper")}</div>
            </div>

            <div className="modal-footer" style={ui.footer}>
                <button
                    type="button"
                    style={{ ...ui.btn, ...(loading ? ui.btnDisabled : null) }}
                    onClick={onClose}
                    disabled={loading}
                >
                    <i className="fas fa-times" />
                    {t("createStripeAccountModal.cancel")}
                </button>

                <button
                    type="button"
                    style={{ ...ui.btn, ...ui.btnPrimary, ...(loading ? ui.btnDisabled : null) }}
                    onClick={handleCreate}
                    disabled={loading}
                >
                    <i className="fas fa-link" />
                    {loading
                        ? t("createStripeAccountModal.creating")
                        : t("createStripeAccountModal.createAndOnboard")}
                </button>
            </div>

            <style>{`
        #${scopeId} .form-control:focus {
          border-color: rgba(0,0,0,0.18);
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #${scopeId} button:focus,
        #${scopeId} a:focus,
        #${scopeId} .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }
      `}</style>
        </div>
    );
}
